/**
 * Service to handle term updates in the claims editor
 */
export class ClaimsTermUpdateService {
    constructor(termsStore, claimsStore) {
        this.termsStore = termsStore;
        this.claimsStore = claimsStore;
    }


    /**
     * Update a term in the spec editor using search service
     */
    updateTermInSpecEditor(oldTerm, newTerm) {
        console.log('[ClaimsTermUpdateService] Updating term in spec editor:', {
            oldTerm,
            newTerm
        });

        // Get the spec editor instance
        const specEditor = window.specEditor;
        if (!specEditor) {
            console.warn('[ClaimsTermUpdateService] Spec editor not found');
            return;
        }

        try {
            // Create a temporary search service instance for the spec editor
            const tempSearchService = {
                editor: specEditor,
                handleReplaceAll: (search, replace) => {
                    // Use word boundaries for accurate matching
                    const searchRegex = new RegExp(`\\b${this.escapeRegExp(search)}\\b`, 'gi');
                    const doc = specEditor.state.doc;
                    const results = [];
                    
                    // Find all matches
                    doc.descendants((node, pos) => {
                        if (node.isText) {
                            const text = node.text;
                            let match;
                            const regex = new RegExp(searchRegex);
                            
                            while ((match = regex.exec(text)) !== null) {
                                results.push({
                                    from: pos + match.index,
                                    to: pos + match.index + match[0].length,
                                    text: match[0]
                                });
                            }
                        }
                    });
                    
                    if (results.length > 0) {
                        // Sort results in reverse order to maintain positions
                        const sortedResults = results.sort((a, b) => b.from - a.from);
                        
                        // Use a single transaction for all replacements
                        const { state } = specEditor;
                        const tr = state.tr;
                        
                        // Replace all occurrences with exact case as specified by user
                        sortedResults.forEach(result => {
                            const replacement = replace;
                            tr.replaceWith(result.from, result.to, state.schema.text(replacement));
                        });
                        
                        // Apply the transaction
                        specEditor.view.dispatch(tr);
                        
                        console.log(`[ClaimsTermUpdateService] Replaced ${results.length} occurrences in spec editor`);
                    }
                }
            };
            
            // Perform the replacement
            tempSearchService.handleReplaceAll(oldTerm, newTerm);
            
        } catch (error) {
            console.error('[ClaimsTermUpdateService] Error updating spec editor:', error);
        }
    }

    /**
     * Update terms based on claims analysis
     * @param {Object} terms - Terms from claims analysis
     * @param {string} content - Current editor content
     * @param {Object} analysisResult - Full result from the backend analysis
     */
    updateTerms(terms, content, analysisResult = {}) {
        console.log('[MANUAL_REFRESH] Final terms update:', {
            termsCount: Object.keys(terms).length,
            refreshType: analysisResult.refresh_type
        });

        // Update refresh state from backend
        if (analysisResult.refresh_type && analysisResult.state_summary) {
            this.termsStore.setRefreshState(analysisResult.refresh_type, analysisResult.state_summary);
        }
        
        // Update claim dependencies if provided
        if (analysisResult.corrected_claim_refs || analysisResult.claim_refs) {
            const claimRefs = analysisResult.corrected_claim_refs || analysisResult.claim_refs;
            // Convert to simpler format for frontend use
            const dependencies = {};
            // Also store raw references (from 'references' field) for recalculation on patent type change
            const rawRefs = {};
            Object.entries(claimRefs).forEach(([claimNum, claimData]) => {
                if (claimData.claim_refs && claimData.claim_refs.length > 0) {
                    dependencies[claimNum] = claimData.claim_refs;
                }
                // Store raw references for recalculation when patent type changes
                const refs = claimData.references || claimData.claim_refs || [];
                if (refs.length > 0) {
                    rawRefs[claimNum] = refs;
                }
            });
            this.termsStore.setRawClaimRefs(rawRefs);
            this.termsStore.setClaimDependencies(dependencies);
            console.log('[ClaimsTermUpdateService] Claim dependencies updated:', JSON.stringify(dependencies, null, 2));
            console.log('[ClaimsTermUpdateService] Raw claim refs stored:', JSON.stringify(rawRefs, null, 2));
        }

        // Handle different refresh types from backend
        if (analysisResult.refresh_type === 'none') {
            console.log('[MANUAL_REFRESH] No refresh needed');
            this.termsStore.setAnalyzingTerms(false);
            return;
        }

        // Standard term loading - preserve indexed structure
        this.termsStore.update(store => {
            // Initialize new termEntries map preserving indexed keys
            const newTermEntries = new Map();
            
            // Add all terms from backend analysis with their indexed keys
            if (typeof terms === 'object' && terms !== null) {
                Object.keys(terms).forEach(indexedKey => {
                    // Store the complete term data under its indexed key
                    // This preserves the context-specific information for each occurrence
                    newTermEntries.set(indexedKey, terms[indexedKey]);
                });
            }
            
            console.log(`[MANUAL_REFRESH] Final update: ${newTermEntries.size} indexed term entries`);
            
            return {
                ...store,
                termEntries: newTermEntries,
                preservedTerms: new Set(), // Clear preserved terms (no longer needed)
                manualTerms: store.manualTerms || new Set(), // Keep manual terms tracking for UI
                isAnalyzingTerms: false
                // Preserve other UI state like highlightedEntries, selectedEntries, expandedEntries
            };
        });
    }

    /**
     * Update terms progressively as they arrive from streaming
     * @param {Object} terms - Current terms from streaming analysis
     * @param {string} content - Current editor content
     * @param {Object} progressData - Progress data from streaming
     */
    updateTermsProgressive(terms, content, progressData = {}) {
        console.log('[MANUAL_REFRESH] Progressive update:', {
            termsCount: Object.keys(terms).length,
            discoveredTerms: progressData.discovered_terms,
            isComplete: progressData.isComplete
        });
        
        this.termsStore.update(store => {
            // Initialize new termEntries map preserving indexed keys
            const newTermEntries = new Map();
            
            // Add all terms from the progressive update with their indexed keys
            if (typeof terms === 'object' && terms !== null) {
                Object.keys(terms).forEach(indexedKey => {
                    // Store the complete term data under its indexed key
                    newTermEntries.set(indexedKey, terms[indexedKey]);
                });
            }
            
            console.log(`[MANUAL_REFRESH] Progressive update: ${newTermEntries.size} indexed term entries`);
            
            return {
                ...store,
                termEntries: newTermEntries,
                manualTerms: store.manualTerms || new Set(), // Keep manual terms tracking for UI
                // Don't set isAnalyzingTerms to false during progressive updates
                lastRefreshType: progressData.refresh_type,
                refreshStateSummary: progressData.state_summary
            };
        });
    }


    /**
     * Escape special characters in string for use in regex
     */
    escapeRegExp(string) {
        return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    }

    /**
     * Clean up subscriptions
     */
    destroy() {
        // Currently no subscriptions to clean up
    }
}
